/**
 * THE VISUAL WORKSPACE.
 *
 * This is the dominant surface of Documents & Drawings, and the one place in
 * OMNΛS where a person looks at the project rather than reading about it.
 *
 * It renders exactly four things and never blurs them:
 *
 *   nothing selected          no claim at all
 *   the record has no file    the row exists; nothing was ever stored
 *   no link was issued        the file is in storage; the URL is not here
 *   the file is here          preview it, or say plainly that we cannot
 *
 * The last line is the one with teeth. A DWG rendered as "preview failed"
 * invites the belief that OMNΛS tried to read a drawing. It did not try, it
 * has no renderer, and there is no backend contract that interprets CAD on
 * current main. So the copy says what did not happen, in those words.
 *
 * NOTHING HERE BUILDS A URL. Every `src` is the backend's own temporary
 * `download_url`; there is no bucket name, no endpoint and no key
 * concatenation anywhere in this feature.
 */
import { useState } from 'react';
import {
  FileText,
  FileImage,
  FileSpreadsheet,
  FileArchive,
  FileType2,
  PencilRuler,
  Presentation,
  File as FileIcon,
  Download,
  ExternalLink,
  ImageOff,
  Layers,
  Loader2,
  Unplug,
  FileQuestion,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { PREVIEW_KIND, noPreviewReason } from './documentPresentation';

/** Icon token → icon. The classification itself lives in the presenter. */
export const ICONS = {
  pdf: FileText,
  image: FileImage,
  sheet: FileSpreadsheet,
  slides: Presentation,
  cad: PencilRuler,
  archive: FileArchive,
  text: FileType2,
  doc: FileText,
  file: FileIcon,
};

export const DocumentTypeIcon = ({ iconKey, className = 'h-5 w-5' }) => {
  const Icon = ICONS[iconKey] || FileIcon;
  return <Icon className={className} aria-hidden="true" />;
};

/**
 * The frame every preview state shares. `label` is what assistive technology
 * hears; the visible copy is the same claim in longer words, so a screen
 * reader and a pair of eyes are told the same thing.
 */
const PreviewFrame = ({
  tone = 'neutral',
  icon: Icon,
  headline,
  detail,
  actions,
  testId,
}) => (
  <div
    data-testid={testId}
    data-tone={tone}
    className="flex h-full min-h-[16rem] w-full flex-col items-center justify-center gap-3 px-5 py-10 text-center"
  >
    {Icon && (
      <Icon className="h-9 w-9 text-muted-foreground/60" aria-hidden="true" />
    )}
    <p className="text-sm font-medium text-foreground">{headline}</p>
    {detail && (
      <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
        {detail}
      </p>
    )}
    {actions && (
      <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
        {actions}
      </div>
    )}
  </div>
);

const FileActions = ({ document: doc, onDownload }) => {
  if (!doc?.downloadUrl) return null;
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onDownload?.(doc)}
        data-testid="document-download-btn"
      >
        <Download className="h-3.5 w-3.5" />
        Download
      </Button>
      <Button variant="ghost" size="sm" asChild data-testid="document-open-btn">
        <a href={doc.downloadUrl} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-3.5 w-3.5" />
          Open in a new tab
        </a>
      </Button>
    </>
  );
};

/**
 * @param {object|null} document a view from readDocumentContract
 * @param {boolean} loading a fresh signed URL is being fetched for a selection
 * @param {object|null} error a classified failure fetching THIS document
 */
export const DocumentPreview = ({
  document: doc,
  loading = false,
  error = null,
  onDownload,
}) => {
  /**
   * A preview that starts and then fails is a fifth state, and it is the
   * browser's report rather than ours — an expired signature, a network drop.
   * It never becomes "the document is missing".
   *
   * The failure is stored AS AN ID rather than as a boolean, so it belongs to
   * the document it happened to. A boolean would survive the next selection
   * and report a perfectly good drawing as broken because the previous one
   * was — the component must not need a `key` from its caller to be correct.
   */
  const [failedForId, setFailedForId] = useState(null);
  const renderFailed = Boolean(doc && failedForId === doc.id);

  const body = (() => {
    if (loading) {
      return (
        <PreviewFrame
          testId="document-preview-loading"
          tone="loading"
          icon={Loader2}
          headline="Opening document…"
          detail="Requesting a temporary link from the document store."
        />
      );
    }

    if (!doc) {
      return (
        <PreviewFrame
          testId="document-preview-empty"
          tone="none"
          icon={Layers}
          headline="Select a document to view it"
          detail="Drawings, photos and PDFs open here. Everything else shows its record and a download."
        />
      );
    }

    if (error) {
      return (
        <PreviewFrame
          testId="document-preview-error"
          tone="failed"
          icon={Unplug}
          headline="This document could not be opened"
          detail={`${error.message || 'The request did not complete.'} The record itself still exists — this is a failure to reach the file, not a missing document.`}
        />
      );
    }

    const { availability } = doc;

    if (!availability.storageHoldsFile) {
      return (
        <PreviewFrame
          testId="document-preview-no-object"
          tone="unknown"
          icon={FileQuestion}
          headline="This record names no stored file"
          detail="The document record exists, but nothing has been stored against it. There is nothing to preview or download."
        />
      );
    }

    if (!availability.signedUrlIssued) {
      return (
        <PreviewFrame
          testId="document-preview-no-link"
          tone="attention"
          icon={Unplug}
          headline="No download link was issued for this file"
          detail="The record exists and names a stored file. The document store did not return a temporary link with this response, so it cannot be shown or downloaded right now. The document has not been lost."
        />
      );
    }

    if (renderFailed) {
      return (
        <PreviewFrame
          testId="document-preview-render-failed"
          tone="failed"
          icon={ImageOff}
          headline="The preview did not load"
          detail="The temporary link was issued but the file did not render — the link may have expired. The record and the file both still exist."
          actions={<FileActions document={doc} onDownload={onDownload} />}
        />
      );
    }

    if (availability.previewKind === PREVIEW_KIND.IMAGE) {
      return (
        <div
          data-testid="document-preview-image"
          data-preview-kind="IMAGE"
          className="flex h-full w-full items-center justify-center overflow-auto bg-black/20 p-2"
        >
          {/*
            An <img> tag, deliberately, including for SVG: an image element
            does not execute script inside an SVG the way an <object> or an
            <iframe> would. The document's own name is the accessible name —
            it is the only description of this image that exists, and it was
            not written by us.
          */}
          <img
            src={doc.downloadUrl}
            alt={doc.name}
            onError={() => setFailedForId(doc.id)}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      );
    }

    if (availability.previewKind === PREVIEW_KIND.PDF) {
      return (
        <div
          data-testid="document-preview-pdf"
          data-preview-kind="PDF"
          className="h-full w-full bg-black/20"
        >
          {/*
            The browser's own PDF viewer, through an iframe with a real title.
            No PDF library is added for this slice: the built-in viewer scrolls,
            zooms and pages on desktop and on a phone, and every alternative
            would be a dependency carrying its own worker bundle.
          */}
          <iframe
            title={`${doc.name} — document preview`}
            src={doc.downloadUrl}
            onError={() => setFailedForId(doc.id)}
            className="h-full min-h-[16rem] w-full border-0"
          />
        </div>
      );
    }

    const reason = noPreviewReason(doc.fileType);
    return (
      <PreviewFrame
        testId="document-preview-unsupported"
        tone="unsupported"
        icon={ICONS[doc.iconKey] || FileIcon}
        headline={reason.headline}
        detail={reason.detail}
        actions={<FileActions document={doc} onDownload={onDownload} />}
      />
    );
  })();

  return (
    <div
      data-testid="document-preview"
      data-document-id={doc?.id || ''}
      data-availability={doc?.availability?.state || 'NONE'}
      className="flex h-full w-full min-w-0 flex-col"
    >
      {/*
        Loading and failure are announced rather than only drawn. A preview
        that quietly swaps under a screen-reader user is a preview they never
        know arrived.
      */}
      <p className="sr-only" role="status" aria-live="polite">
        {loading
          ? 'Opening document'
          : doc
            ? `${doc.name}. ${doc.availability.display.label}.`
            : 'No document selected'}
      </p>
      <div className="min-h-0 flex-1">{body}</div>
    </div>
  );
};

export default DocumentPreview;
